import { Suspense, lazy } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import { AppShellLayout } from "./components/AppShellLayout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Spinner } from "./components/Spinner";
import {
  RequireAdmin,
  RequireAuth,
  RequireManager,
} from "./components/RequireAuth";
// Login stays eagerly bundled: it's the first paint for signed-out users and
// shouldn't wait on a second chunk. Everything else code-splits per route so
// the entry bundle stays under Vite's 500 kB warning line.
import { Login } from "./routes/Login";

const AdminAudit = lazy(() =>
  import("./routes/AdminAudit").then((m) => ({ default: m.AdminAudit })),
);
const AdminDepartments = lazy(() =>
  import("./routes/AdminDepartments").then((m) => ({ default: m.AdminDepartments })),
);
const AdminHolidays = lazy(() =>
  import("./routes/AdminHolidays").then((m) => ({ default: m.AdminHolidays })),
);
const AdminInvites = lazy(() =>
  import("./routes/AdminInvites").then((m) => ({ default: m.AdminInvites })),
);
const AdminSettings = lazy(() =>
  import("./routes/AdminSettings").then((m) => ({ default: m.AdminSettings })),
);
const AdminUsers = lazy(() =>
  import("./routes/AdminUsers").then((m) => ({ default: m.AdminUsers })),
);
const Home = lazy(() =>
  import("./routes/Home").then((m) => ({ default: m.Home })),
);
const CreateOrg = lazy(() =>
  import("./routes/onboarding/CreateOrg").then((m) => ({ default: m.CreateOrg })),
);
const Manage = lazy(() =>
  import("./routes/Manage").then((m) => ({ default: m.Manage })),
);
const ManageQueue = lazy(() =>
  import("./routes/ManageQueue").then((m) => ({ default: m.ManageQueue })),
);
const ManageSchedule = lazy(() =>
  import("./routes/ManageSchedule").then((m) => ({ default: m.ManageSchedule })),
);
const ManageTemplates = lazy(() =>
  import("./routes/ManageTemplates").then((m) => ({ default: m.ManageTemplates })),
);
const MySchedule = lazy(() =>
  import("./routes/MySchedule").then((m) => ({ default: m.MySchedule })),
);
const Notifications = lazy(() =>
  import("./routes/Notifications").then((m) => ({ default: m.Notifications })),
);
const NotFound = lazy(() =>
  import("./routes/NotFound").then((m) => ({ default: m.NotFound })),
);
const OpenShifts = lazy(() =>
  import("./routes/OpenShifts").then((m) => ({ default: m.OpenShifts })),
);
const Pending = lazy(() =>
  import("./routes/Pending").then((m) => ({ default: m.Pending })),
);
const Preferences = lazy(() =>
  import("./routes/Preferences").then((m) => ({ default: m.Preferences })),
);
const Profile = lazy(() =>
  import("./routes/Profile").then((m) => ({ default: m.Profile })),
);
const Swaps = lazy(() =>
  import("./routes/Swaps").then((m) => ({ default: m.Swaps })),
);
const TimeOff = lazy(() =>
  import("./routes/TimeOff").then((m) => ({ default: m.TimeOff })),
);

function RouteFallback() {
  return (
    <div className="grid min-h-[40vh] place-items-center" role="status">
      <Spinner size={22} className="text-fg-2" />
      <span className="sr-only">Loading page</span>
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route element={<RequireAuth />}>
                {/* Pending and onboarding render without the global shell —
                    they're forced-route destinations until the user is fully
                    provisioned. */}
                <Route path="/pending" element={<Pending />} />
                <Route path="/onboarding/create-org" element={<CreateOrg />} />
                <Route element={<AppShellLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/preferences" element={<Preferences />} />
                  <Route path="/my-schedule" element={<MySchedule />} />
                  <Route path="/swaps" element={<Swaps />} />
                  <Route path="/time-off" element={<TimeOff />} />
                  <Route path="/open-shifts" element={<OpenShifts />} />
                  <Route path="/notifications" element={<Notifications />} />
                </Route>
              </Route>
              <Route element={<RequireManager />}>
                <Route element={<AppShellLayout />}>
                  <Route path="/manage" element={<Manage />} />
                  <Route path="/manage/queue" element={<ManageQueue />} />
                  <Route path="/manage/schedule" element={<ManageSchedule />} />
                  <Route path="/manage/templates" element={<ManageTemplates />} />
                </Route>
              </Route>
              <Route element={<RequireAdmin />}>
                <Route element={<AppShellLayout />}>
                  <Route path="/admin/departments" element={<AdminDepartments />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/invites" element={<AdminInvites />} />
                  <Route path="/admin/holidays" element={<AdminHolidays />} />
                  <Route path="/admin/audit" element={<AdminAudit />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                </Route>
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}
